/**
 * Classify detected scheduling / CRM / EMR / PMS platforms into MCP integration paths.
 * Path 1 = supported registry MCP; Path 2 = interim no custom MCP; Path 3 = custom MCP request.
 */

import {
  MARKETPLACE_SCHEDULING_INTEGRATIONS,
  type MarketplaceSchedulingIntegrationId,
} from "~/lib/marketplace/hire-pricing";
import type { IntegrationPathKind } from "~/lib/siteglue/consultant-onboarding";

export type SupportedMcpPlatform = {
  id: string;
  label: string;
  kind: "scheduling" | "emr" | "comms";
  capabilities: string[];
  registryId: string;
};

/** Capabilities shown when Path 1 is selected — keep honest to product behavior. */
const CAPABILITIES: Record<string, string[]> = {
  google_calendar: [
    "List calendars",
    "Check availability",
    "Create calendar events",
    "Human-in-the-Loop™ handoff",
  ],
  calendly: [
    "List event types",
    "Check available times",
    "Create invitees",
    "Human-in-the-Loop™ handoff",
  ],
  zoom: ["Include Zoom meeting links on bookings when configured"],
  juvonno: [
    "Patient lookup / create",
    "Practitioner availability",
    "Book / reschedule / cancel",
    "Location and service selection",
  ],
  nexhealth: [
    "Patient lookup / create",
    "Provider availability",
    "Book appointments",
    "Location and appointment types",
  ],
};

/**
 * Source of truth for Path 1 — derived from hire marketplace registries.
 * Do not treat the static comment list as authoritative; prefer this helper.
 */
export function getSupportedMcpPlatforms(): SupportedMcpPlatform[] {
  const scheduling = MARKETPLACE_SCHEDULING_INTEGRATIONS.filter(
    (i) => i.id !== "none" && i.id !== "juvonno" && !String(i.id).includes("dental"),
  ).map((i) => ({
    id: i.id,
    label: i.label,
    kind: (i.id === "zoom" ? "comms" : "scheduling") as SupportedMcpPlatform["kind"],
    capabilities: CAPABILITIES[i.id] ?? ["Connect via SiteGlue Tools"],
    registryId: i.id,
  }));

  /** Confirmed MCP HTTP routes in this codebase (chat/phone). */
  const confirmedMcp: SupportedMcpPlatform[] = [
    {
      id: "juvonno",
      label: "Juvonno",
      kind: "emr",
      capabilities: CAPABILITIES.juvonno ?? [],
      registryId: "juvonno",
    },
    {
      id: "nexhealth",
      label: "NexHealth",
      kind: "emr",
      capabilities: CAPABILITIES.nexhealth ?? [],
      registryId: "nexhealth",
    },
  ];

  const byId = new Map<string, SupportedMcpPlatform>();
  for (const p of [...scheduling, ...confirmedMcp]) {
    byId.set(p.id, p);
  }
  return Array.from(byId.values());
}

export function findSupportedMcpPlatform(
  platformName: string | null | undefined,
): SupportedMcpPlatform | null {
  if (!platformName?.trim()) return null;
  const q = platformName.trim().toLowerCase();
  return (
    getSupportedMcpPlatforms().find(
      (p) =>
        p.id === q ||
        p.label.toLowerCase() === q ||
        q.includes(p.label.toLowerCase()) ||
        p.label.toLowerCase().includes(q),
    ) ?? null
  );
}

/** Platforms commonly detected on websites that are NOT confirmed MCP-supported. */
export const UNSUPPORTED_BUT_KNOWN_PLATFORMS = [
  "Jane",
  "Dentrix",
  "Open Dental",
  "Eaglesoft",
  "Mindbody",
  "Acuity",
  "Square Appointments",
  "GoHighLevel",
  "HubSpot",
  "Microsoft Outlook",
  "Cliniko",
  "DrChrono",
  "Athenahealth",
  "ChiroTouch",
] as const;

export type IntegrationPathResult = {
  path: IntegrationPathKind;
  platform: string | null;
  supported: SupportedMcpPlatform | null;
  explanation: string;
  primaryActionLabel: string;
  interimWorkflow: string | null;
  limitations: string[];
};

export function classifyIntegrationPath(input: {
  detectedSystem: string | null;
  schedulingRelevant: boolean;
  schedulingLevel: "none" | "appointment_request" | "basic" | "advanced" | "enterprise";
  hasBookingLink?: boolean;
  phoneOnly?: boolean;
}): IntegrationPathResult {
  if (!input.schedulingRelevant || input.schedulingLevel === "none") {
    return {
      path: "interim",
      platform: null,
      supported: null,
      explanation:
        "Appointment scheduling does not appear to be a primary need, so a scheduling MCP is not required.",
      primaryActionLabel: "Configure Appointment Workflow",
      interimWorkflow: "No scheduling workflow — use Team Handoff™ for complex requests.",
      limitations: [],
    };
  }

  const supported = findSupportedMcpPlatform(input.detectedSystem);
  if (supported) {
    return {
      path: "supported",
      platform: supported.label,
      supported,
      explanation: `I found signals for ${supported.label}, which is available as a SiteGlue MCP integration.`,
      primaryActionLabel: "Connect Integration",
      interimWorkflow: null,
      limitations: [],
    };
  }

  const knownUnsupported = UNSUPPORTED_BUT_KNOWN_PLATFORMS.find((p) =>
    (input.detectedSystem ?? "").toLowerCase().includes(p.toLowerCase()),
  );

  if (knownUnsupported || (input.detectedSystem && input.detectedSystem !== "Phone-only scheduling")) {
    const platform = knownUnsupported ?? input.detectedSystem ?? "your platform";
    return {
      path: "custom_mcp",
      platform,
      supported: null,
      explanation: `I found that you use ${platform}. A direct SiteGlue integration is not currently confirmed. I can prepare a custom integration request for our development team. In the meantime, SiteGlue can collect appointment requests and send them to your team.`,
      primaryActionLabel: "Request Custom Integration",
      interimWorkflow: input.hasBookingLink
        ? "Use existing booking link + appointment request capture with human confirmation"
        : "Appointment request capture with email notification and human follow-up",
      limitations: [
        "No real-time availability until a direct integration is approved",
        "Bookings require team confirmation unless an interim booking link is used",
      ],
    };
  }

  // Phone-only / no system — interim Path 2
  return {
    path: "interim",
    platform: input.detectedSystem ?? "Phone-only / undetected",
    supported: null,
    explanation: input.phoneOnly
      ? "Scheduling appears phone-driven. We can capture appointment requests and notify your team without a custom MCP."
      : "No supported scheduling platform was confirmed. An appointment-request or booking-link workflow meets most needs without custom development.",
    primaryActionLabel: "Configure Appointment Workflow",
    interimWorkflow: input.hasBookingLink
      ? "Use existing booking link with AI intake"
      : "Appointment request capture → email team → human confirmation",
    limitations: [
      "AI will not write directly into an unsupported PMS until Path 1 or a custom MCP is available",
    ],
  };
}

export type CustomMcpDiscoveryField =
  | "platformWebsite"
  | "apiDocsUrl"
  | "requiredWorkflows"
  | "readWriteNeeds"
  | "authMethodKnown"
  | "sandboxAvailable"
  | "expectedUsage"
  | "desiredTimeline"
  | "interimPreference"
  | "contactEmail";

export const CUSTOM_MCP_DISCOVERY_ORDER: {
  field: CustomMcpDiscoveryField;
  question: string;
}[] = [
  {
    field: "platformWebsite",
    question: "What is the platform’s website (if you know it)?",
  },
  {
    field: "apiDocsUrl",
    question: "Do you have an API documentation URL? If not, say “none”.",
  },
  {
    field: "requiredWorkflows",
    question:
      "Which workflows do you need? (e.g. patient lookup, availability, book, reschedule, cancel)",
  },
  {
    field: "readWriteNeeds",
    question: "Do you need read-only access, write/booking access, or both?",
  },
  {
    field: "authMethodKnown",
    question:
      "Do you know the authentication method (OAuth, API key, etc.)? Do not share secrets—just the method if known.",
  },
  {
    field: "sandboxAvailable",
    question: "Is a sandbox or demo environment available?",
  },
  {
    field: "expectedUsage",
    question: "Roughly how many appointments or scheduling conversations per month?",
  },
  {
    field: "desiredTimeline",
    question: "What timeline are you hoping for?",
  },
  {
    field: "interimPreference",
    question:
      "While we review a custom integration, prefer: booking link, appointment requests, or human follow-up?",
  },
  {
    field: "contactEmail",
    question: "What email should we use for updates on this request?",
  },
];

export type CustomMcpRequestPayload = {
  businessName: string;
  customerName: string;
  customerEmail: string;
  platform: string;
  platformWebsite?: string;
  apiDocsUrl?: string;
  locations: string[];
  requiredWorkflows: string;
  readWriteNeeds: string;
  authMethodKnown?: string;
  sandboxAvailable?: string;
  expectedUsage?: string;
  desiredTimeline?: string;
  interimWorkflow: string;
  recommendedPlan?: string;
  websiteUrl?: string;
  openQuestions: string[];
  businessJustification: string;
};
